import type { GameId, MissionId, PlayerId, RewardId, SessionId } from './ids';

/**
 * What one session has already contributed to this progress (R2-1).
 *
 * A sitting a grown-up ended safely keeps its resume snapshot, and "Continue playing" carries on
 * under the *same* session id (D-041(2)/(3)). So one logical session can complete more than once,
 * and without a record of what it already added the second completion folded the whole running
 * total in again — twelve shards, then thirty-five more, and two missions played for one evening.
 *
 * Progress therefore remembers what it has applied per session and supersedes rather than
 * accumulates. It is a bookkeeping record, not a score: it is never shown to anybody, never
 * compared between children, and carries no timing (architecture §12).
 */
export interface AppliedSessionRecord {
  sessionId: SessionId;
  /** The team total this session had contributed the last time it was applied. */
  teamScore: number;
}

/**
 * How many sessions' bookkeeping is kept.
 *
 * Only one interrupted mission exists at a time (`MissionProgressRepository` is a single slot),
 * so only the most recent session can ever be continued — a handful of entries is already far
 * more than the recovery path can use, and an unbounded list in local storage is a leak.
 */
export const APPLIED_SESSION_HISTORY_LIMIT = 8;

/**
 * The counter names durable game progress will accept (S1-3, S1-2).
 *
 * The exact sibling of `MISSION_SCORE_DETAIL_KEYS` and it exists for the same reason: `counters`
 * is open-ended in the SDK contract because a game owns its own currency, but a persisted open map
 * accepts caller-controlled *key* text, and D-028 settled that an object key is as much a payload
 * as an object value — `{"data:image/png;base64,…": 1, "poseX": 0.42}` is a media marker and a
 * coordinate series wearing a counter's clothes. A game adding a counter it wants to keep adds it
 * here, which is a reviewable diff rather than a silent widening of what storage will accept.
 *
 * A counter that is not listed still works at runtime; it simply is not stored.
 */
export const GAME_PROGRESS_COUNTER_KEYS = [
  // Spider Math's progression counters.
  'numberShards',
  'missionsPlayed',
] as const;

/** Game-owned progression state. Thrive-level achievements are stored separately. */
export interface GameProgressSnapshot {
  gameId: GameId;
  playerId: PlayerId;
  /** Module-owned schema version so a game can migrate its own progress shape. */
  schemaVersion: number;
  completedMissionIds: MissionId[];
  rewards: GameRewardState;
  /** Game-specific counters (e.g. number shards). Never used for cross-child comparison. */
  counters: Record<string, number>;
  /**
   * Platform-owned bookkeeping: what each recent session has already contributed (R2-1).
   *
   * Written by the mission runtime's progression transaction, read by nothing else. A module
   * never has to maintain it; it only has to honour the `previouslyApplied` figure the platform
   * hands `applySession`.
   */
  appliedSessions?: AppliedSessionRecord[];
  /** ISO-8601 timestamp. */
  updatedAt: string;
}

export interface GameRewardState {
  unlockedRewardIds: RewardId[];
  /** Highest positive badge earned; never a failure tier. */
  badgeId?: RewardId;
}
